import {
  isOrderStrongReportReadModel,
  type OrderStrongReportFilter,
  type OrderStrongReportReadModel,
} from "@billiard/contracts";
import { ApiRequestError, requestOperatorJson } from "./api";

export type OrderReceivableBusinessMetricDetailRoute =
  | { kind: "NONE" }
  | { kind: "INVALID" }
  | { kind: "DETAIL"; businessDate: string };

export interface OrderReceivableBusinessMetricDetailOperatorContext {
  tenantId: string;
  storeId: string;
  operatorId: string;
}

export type OrderReceivableBusinessMetricDetailQueryKey = readonly [
  "order-receivable-business-metric-detail",
  string,
  string,
  string,
  string,
];

function isCanonicalBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function contextIsCanonical(context: OrderReceivableBusinessMetricDetailOperatorContext): boolean {
  return [context.tenantId, context.storeId, context.operatorId].every(
    (value) => value.length >= 1 && value.length <= 128 && value.trim() === value,
  );
}

function requestInvalid(): ApiRequestError {
  return new ApiRequestError(
    "REQUEST_VALIDATION_FAILED",
    "订单应收明细的营业日期或当前操作身份不正确，系统没有读取明细",
    400,
  );
}

function exactBusinessDateFilters(businessDate: string): OrderStrongReportFilter {
  return {
    offlineOnly: false,
    dateFrom: businessDate,
    dateTo: businessDate,
    orderQuery: "",
    tableNumber: "",
    productQuery: "",
    memberSearch: "",
    paymentMethod: null,
    openingMethod: null,
    shiftId: null,
  };
}

export function orderReceivableBusinessMetricDetailRouteFromSearch(
  search: string,
): OrderReceivableBusinessMetricDetailRoute {
  const params = new URLSearchParams(search);
  const views = params.getAll("view");
  if (views.length === 0) return { kind: "NONE" };

  const businessDates = params.getAll("businessDate");
  if (
    [...params.entries()].length !== 2 ||
    views.length !== 1 ||
    views[0] !== "ORDER_RECEIVABLE_DETAILS" ||
    businessDates.length !== 1
  ) {
    return { kind: "INVALID" };
  }
  const businessDate = businessDates[0] ?? "";
  return isCanonicalBusinessDate(businessDate)
    ? { kind: "DETAIL", businessDate }
    : { kind: "INVALID" };
}

export function orderReceivableBusinessMetricDetailQueryKey(
  currentOperatorContext: OrderReceivableBusinessMetricDetailOperatorContext,
  businessDate: string,
): OrderReceivableBusinessMetricDetailQueryKey {
  if (!isCanonicalBusinessDate(businessDate) || !contextIsCanonical(currentOperatorContext)) {
    throw requestInvalid();
  }
  return [
    "order-receivable-business-metric-detail",
    currentOperatorContext.tenantId,
    currentOperatorContext.storeId,
    currentOperatorContext.operatorId,
    businessDate,
  ];
}

export async function getOrderReceivableBusinessMetricDetail(
  businessDate: string,
  currentOperatorContext: OrderReceivableBusinessMetricDetailOperatorContext,
  signal?: AbortSignal,
): Promise<OrderStrongReportReadModel> {
  if (!isCanonicalBusinessDate(businessDate) || !contextIsCanonical(currentOperatorContext)) {
    throw requestInvalid();
  }
  const filters = exactBusinessDateFilters(businessDate);
  const query = new URLSearchParams({ metricSource: "ORDER_RECEIVABLE", businessDate });
  const value = await requestOperatorJson<unknown>(`/v1/orders/report?${query.toString()}`, {
    cache: "no-store",
    ...(signal ? { signal } : {}),
  });
  if (
    !isOrderStrongReportReadModel(value, {
      tenantId: currentOperatorContext.tenantId,
      storeId: currentOperatorContext.storeId,
      filters,
    })
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "订单应收明细无法安全确认，页面已隐藏旧明细和金额，请重新读取",
    );
  }
  return value;
}
